import { Language } from '../types';
import defaultLogo from '../assets/images/api_heat_logo_360.webp';
import defaultHeroBg from '../assets/images/home_hero_boiler_1789081283486.webp';
import expertPlumberImg from '../assets/images/expert_plombier_chaudiere_800.webp';

export { defaultLogo, defaultHeroBg, expertPlumberImg };

export const DEFAULT_SERVICE_IMAGES: Record<string, string> = {
  plumbing: expertPlumberImg,
  heating: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?q=75&w=640&auto=format&fit=crop',
  unblocking: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?q=75&w=640&auto=format&fit=crop',
  ventilation: 'https://images.unsplash.com/photo-1590402494682-cd3fb53b1f70?q=75&w=640&auto=format&fit=crop',
  'water-softener': 'https://images.unsplash.com/photo-1585421514284-efb74c2b69ba?q=75&w=640&auto=format&fit=crop'
};

export interface PageImages {
  equipmentBanner: string;
  aboutImage: string;
  zonesBanner: string;
  contactImage: string;
}

export const DEFAULT_PAGE_IMAGES: PageImages = {
  equipmentBanner: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?q=75&w=800&auto=format&fit=crop',
  aboutImage: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?q=75&w=800&auto=format&fit=crop',
  zonesBanner: 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?q=75&w=800&auto=format&fit=crop',
  contactImage: 'https://images.unsplash.com/photo-1542013936693-884638332954?q=75&w=800&auto=format&fit=crop'
};

export interface SiteConfig {
  phone: string;
  gsm: string;
  email: string;
  address: string;
  vat: string;
  director: string;
  headerLogo: string;
  headerLogoSize: number; // Taille en pixels (ex: 48, 56, 64, 72, 80, 96, 112)
  heroImage: string;
  footerBio: Record<Language, string>;
  emergencyBannerText: Record<Language, string>;
  serviceImages: Record<string, string>;
  pageImages: PageImages;
}

export const LOGO_SIZES = [
  { value: 40, label: '40px (Compact)', className: 'w-10 h-10' },
  { value: 48, label: '48px (Standard)', className: 'w-12 h-12' },
  { value: 56, label: '56px (Moyen)', className: 'w-14 h-14' },
  { value: 64, label: '64px (Grand)', className: 'w-16 h-16' },
  { value: 72, label: '72px (Très Grand)', className: 'w-[72px] h-[72px]' },
  { value: 80, label: '80px (XL)', className: 'w-20 h-20' },
  { value: 96, label: '96px (XXL)', className: 'w-24 h-24' },
  { value: 112, label: '112px (Maxi)', className: 'w-28 h-28' },
];

export function getLogoSizeClass(size?: number): string {
  const match = LOGO_SIZES.find(s => s.value === size);
  if (match) return match.className;
  if (!size || size <= 44) return 'w-10 h-10';
  if (size <= 52) return 'w-12 h-12';
  if (size <= 60) return 'w-14 h-14';
  if (size <= 68) return 'w-16 h-16';
  if (size <= 76) return 'w-[72px] h-[72px]';
  if (size <= 88) return 'w-20 h-20';
  if (size <= 104) return 'w-24 h-24';
  return 'w-28 h-28';
}

const DEFAULT_CONFIG: SiteConfig = {
  phone: '0493 37 37 89',
  gsm: '0493 37 37 89',
  email: 'info@apiheat.be',
  address: 'Bergensesteenweg 24/8, 1600 Sint-Pieters-Leeuw',
  vat: 'BE0794.514.033',
  director: 'Ilias El Filali',
  headerLogo: defaultLogo,
  headerLogoSize: 48,
  heroImage: defaultHeroBg,
  footerBio: {
    fr: 'Leader belge des services d\'urgence en plomberie, chauffage et débouchage 24/7. Techniciens certifiés par ELF DISTRICT.',
    nl: 'Belgische marktleider in spoeddiensten voor loodgieterij, verwarming en ontstopping 24/7. Gecertificeerde technici door ELF DISTRICT.',
    en: 'Belgium\'s leading 24/7 emergency plumbing, heating, and drain unblocking services by ELF DISTRICT.'
  },
  emergencyBannerText: {
    fr: 'Intervention Express 24/7 en Belgique - Arrivée en 30 min chrono',
    nl: 'Snelle Interventie 24/7 in België - Binnen 30 min ter plaatse',
    en: '24/7 Express Intervention in Belgium - Arriving in 30 mins'
  },
  serviceImages: DEFAULT_SERVICE_IMAGES,
  pageImages: DEFAULT_PAGE_IMAGES
};

const STORAGE_KEY = 'api_heat_site_config_v1';

let cachedConfig: SiteConfig | null = null;

export function normalizeConfig(parsed: any): SiteConfig {
  const email = (!parsed.email || parsed.email === 'contactapiheat@gmail.com') ? 'info@apiheat.be' : parsed.email;
  // Numéro de téléphone officiel immuable
  const OFFICIAL_PHONE = '0493 37 37 89';
  return {
    ...DEFAULT_CONFIG,
    ...parsed,
    phone: OFFICIAL_PHONE,
    gsm: OFFICIAL_PHONE,
    email,
    headerLogoSize: typeof parsed.headerLogoSize === 'number' ? parsed.headerLogoSize : 48,
    serviceImages: {
      ...DEFAULT_SERVICE_IMAGES,
      ...(parsed.serviceImages || {})
    },
    pageImages: {
      ...DEFAULT_PAGE_IMAGES,
      ...(parsed.pageImages || {})
    }
  };
}

export function getSiteConfig(): SiteConfig {
  if (cachedConfig) {
    return cachedConfig;
  }
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      cachedConfig = normalizeConfig(JSON.parse(saved));
      return cachedConfig;
    }
  } catch (e) {
    console.error('Failed to load site config', e);
  }
  cachedConfig = DEFAULT_CONFIG;
  return DEFAULT_CONFIG;
}

/**
 * Récupère la configuration stockée directement sur le serveur.
 * Permet à TOUS les utilisateurs (visiteurs) de voir instantanément
 * les photos et logos modifiés depuis le panneau d'administration.
 */
export async function fetchServerSiteConfig(): Promise<SiteConfig> {
  try {
    const res = await fetch('/api/site-config');
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.config && Object.keys(data.config).length > 0) {
        const merged = normalizeConfig(data.config);
        cachedConfig = merged;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
        // Keep master local backup updated
        try {
          localStorage.setItem('apiheat_permanent_master_backup_v1', JSON.stringify({
            version: '1.0',
            exportedAt: new Date().toISOString(),
            source: 'Auto-sync from server',
            config: merged
          }));
        } catch {}
        window.dispatchEvent(new Event('siteConfigUpdated'));
        return merged;
      }
    }
  } catch (e) {
    console.warn('[SiteConfig] Erreur récupération configuration serveur:', e);
  }

  // Self-Healing: If server has no custom config, check if we have a local permanent backup
  try {
    const rawBackup = localStorage.getItem('apiheat_permanent_master_backup_v1');
    if (rawBackup) {
      const parsed = JSON.parse(rawBackup);
      const backupConfig = parsed.config || parsed;
      if (backupConfig && typeof backupConfig === 'object') {
        const restored = normalizeConfig(backupConfig);
        cachedConfig = restored;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(restored));
        // Push back to newly deployed server in the background
        fetch('/api/site-config', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ config: restored })
        }).catch(() => {});
        window.dispatchEvent(new Event('siteConfigUpdated'));
        return restored;
      }
    }
  } catch (err) {
    console.warn('[SiteConfig] Self-healing error:', err);
  }

  return getSiteConfig();
}

/**
 * Enregistre la configuration directement sur le serveur Node.js.
 * La configuration est écrite dans data/site-config.json et devient
 * immédiatement valable pour TOUS les visiteurs du site.
 */
export async function saveSiteConfigToServer(config: SiteConfig): Promise<{ success: boolean; message?: string }> {
  cachedConfig = config;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    // Permanent master backup in browser
    localStorage.setItem('apiheat_permanent_master_backup_v1', JSON.stringify({
      version: '1.0',
      exportedAt: new Date().toISOString(),
      source: 'Admin Save Action',
      config
    }));
    window.dispatchEvent(new Event('siteConfigUpdated'));
  } catch (e) {
    console.error('Failed to save site config locally', e);
  }

  try {
    const res = await fetch('/api/site-config', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ config })
    });

    const data = await res.json();
    if (data.success) {
      return {
        success: true,
        message: data.message || 'Configuration enregistrée sur le serveur pour tous les utilisateurs'
      };
    }
    return {
      success: false,
      message: data.error || 'Erreur lors de la sauvegarde sur le serveur'
    };
  } catch (err: any) {
    console.error('[SiteConfig] Erreur réseau lors de la sauvegarde serveur:', err);
    return {
      success: false,
      message: err.message || 'Erreur réseau avec le serveur'
    };
  }
}

/**
 * Téléverse directement un fichier image sur le serveur.
 * L'image est enregistrée dans data/uploads/ et rendue accessible
 * via une URL permanente /uploads/nom_de_fichier.ext pour tous les utilisateurs.
 */
export async function uploadImageToServer(file: File): Promise<{ success: boolean; url?: string; message?: string }> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => {
      resolve({ success: false, message: 'Échec de lecture du fichier image' });
    };
    reader.onloadend = async () => {
      try {
        const base64Data = reader.result as string;
        const res = await fetch('/api/upload-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageBase64: base64Data,
            filename: file.name
          })
        });

        const data = await res.json();
        if (data.success && data.url) {
          resolve({ success: true, url: data.url });
        } else {
          resolve({ success: false, message: data.error || 'Erreur lors du téléversement sur le serveur' });
        }
      } catch (err: any) {
        resolve({ success: false, message: err.message || 'Erreur de connexion avec le serveur' });
      }
    };
    reader.readAsDataURL(file);
  });
}

export function saveSiteConfig(config: SiteConfig): void {
  saveSiteConfigToServer(config);
}
